import {
  MAX_TRAIN_NUMBER_LENGTH,
  MAX_TRAIN_NUMBERS,
  TRAIN_NUMBER_PATTERN,
} from '../../../utils/broadcastContents';

/**
 * 入力テキスト (改行 / カンマ / 読点 / 空白区切り) を列番の配列へ分解する。
 *
 * 運用者は Excel からのコピペや手打ちで列番を並べるため、区切り文字を 1 種類に
 * 強制すると入力事故が起きる。ここで広めに受けて分解し、`onChange` には
 * 「正規化済みの配列」を渡す (保存形は常に配列 = サーバの受け口と一致)。
 *
 * 前後空白の除去と重複除去 (英字を大文字化して比較) はサーバ側
 * (`_coerce_train_numbers`) と同じ規則にしてあるため、画面表示と保存結果がずれない。
 * ただし **文字種の検証はここでは落とさない** (不正値を黙って消すと運用者が
 * 「入れたのに消えた」と混乱するため)。不正値は下の警告表示で明示する。
 */
export function parseTrainNumbersInput(raw: string): string[] {
  const tokens = raw.split(/[\s,、，\n\r]+/);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const token of tokens) {
    const trimmed = token.trim();
    if (trimmed === '') continue;
    const key = trimmed.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

/** サーバ側の検証 (`_coerce_train_numbers`) で 400 になる要素を抽出する。 */
export function invalidTrainNumbers(values: readonly string[]): string[] {
  return values.filter(
    (value) => value.length > MAX_TRAIN_NUMBER_LENGTH || !TRAIN_NUMBER_PATTERN.test(value),
  );
}

/**
 * コンテンツ単位の「放送対象の列車番号 (列番)」入力欄 (Issue #530)。
 *
 * - 受け入れ条件「列車番号で放送対象の列車を設定できること」「複数の列車番号を
 *   指定できること」に対応する。空欄 = 全列車 (従来動作) で、値を入れた場合のみ
 *   その列番の列車だけで放送される。
 * - 入力は `<textarea>` の 1 欄に集約し、改行 / カンマ / 読点 / 空白のいずれでも
 *   区切れるようにする (Excel からのコピペを想定)。親には常に **正規化済みの配列**
 *   を渡すため、保存形 (`string[]`) がブレない。
 * - 空配列になった場合は親側で `train_numbers` プロパティを削除する
 *   (未指定と同義 = DynamoDB item 削減 + 既存データと bit-perfect 互換)。
 *   サーバ側 (`_coerce_train_numbers`) も空配列を `None` に落として保存しないため、
 *   フロント / サーバで挙動が一致する。
 * - **判定はサーバ側 (UpdateContents Lambda) で行う**。`trip_id` の列番セグメントと
 *   照合し、一致しないコンテンツは配信自体を行わない (詳細は `docs/train-number.md`)。
 *
 * a11y: `<textarea>` に `aria-label` を付与し、件数・上限・不正値を
 * テキストで明示する (色だけに頼らない)。
 */
export function TrainNumbersField({
  contentIndex,
  value,
  onChange,
}: {
  /** 親コンテンツの index (data-testid 用) */
  contentIndex: number;
  /** 現在の train_numbers (undefined / 空配列 = 未指定 = 全列車) */
  value: string[] | undefined;
  /** 値が変わったら呼ばれる (空配列 = 未指定に戻す) */
  onChange: (nextNumbers: string[]) => void;
}): React.JSX.Element {
  const current = value ?? [];
  // textarea には「1 行 1 列番」で見せる (区切り文字の混在を運用者に意識させない)。
  const text = current.join('\n');
  const invalid = invalidTrainNumbers(current);
  const overLimit = current.length > MAX_TRAIN_NUMBERS;

  return (
    // TrainTypeField / PlaybackRateField と同じ「見出し + 入力 + ヒント」レイアウト。
    // textarea は縦に伸びるので見出しは上揃えにする。
    <div
      className="train-numbers-field"
      data-testid={`content-train-numbers-field-${contentIndex}`}
      style={{
        margin: '6px 0',
        padding: '6px 8px',
        background: '#f8f9fa',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      <strong style={{ paddingTop: 4 }}>列車番号</strong>
      <textarea
        aria-label={`コンテンツ#${contentIndex + 1} 放送対象の列車番号`}
        data-testid={`content-train-numbers-input-${contentIndex}`}
        className={invalid.length > 0 || overLimit ? 'is-invalid' : undefined}
        value={text}
        rows={3}
        placeholder={'1001A\n1003A\n2204B'}
        onChange={(e) => onChange(parseTrainNumbersInput(e.target.value))}
        style={{ flex: '1 1 200px', minWidth: 160, fontFamily: 'monospace' }}
      />
      <span
        className="hint"
        data-testid={`content-train-numbers-hint-${contentIndex}`}
        style={{ fontSize: 12, color: '#666', flex: '1 1 100%' }}
      >
        {current.length === 0
          ? '(空欄=全列車。列番を入れるとその列車のみ放送します。改行・カンマ・空白区切りで複数指定できます)'
          : `(${current.length}件を指定中 / 最大${MAX_TRAIN_NUMBERS}件。改行・カンマ・空白区切りで複数指定できます)`}
      </span>
      {invalid.length > 0 && (
        <span
          role="alert"
          data-testid={`content-train-numbers-error-${contentIndex}`}
          style={{ fontSize: 12, color: '#c0392b', flex: '1 1 100%' }}
        >
          {`列番に使えない値があります (半角英数字とハイフンのみ / ${MAX_TRAIN_NUMBER_LENGTH}文字以内): ${invalid.join(', ')}`}
        </span>
      )}
      {overLimit && (
        <span
          role="alert"
          data-testid={`content-train-numbers-over-limit-${contentIndex}`}
          style={{ fontSize: 12, color: '#c0392b', flex: '1 1 100%' }}
        >
          {`列番は最大${MAX_TRAIN_NUMBERS}件までです (現在${current.length}件)`}
        </span>
      )}
    </div>
  );
}
